import { useContext, useEffect } from 'react';
import { SocketContext, SocketContextType, SocketEventType, SocketEventHandler } from '../contexts/SocketContext';

/**
 * Core socket hooks
 * 
 * - useSocket(): Access socket context (socket instance, connection state, emit, etc.)
 * - useSocketEvent(): Subscribe to a specific socket event with automatic cleanup
 * 
 * For convenience hooks (useNewMessage, useSenderListChanged, etc.), 
 * see './useSocketEvents'
 */

/**
 * Hook to access the socket context
 * @returns Socket context with socket instance, connection state, emit, on, off methods
 * @throws Error if used outside SocketProvider
 * 
 * @example
 * ```tsx
 * const { socket, isConnected, emit } = useSocket();
 * ```
 */
export function useSocket(): SocketContextType {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
}

/**
 * Hook to subscribe to a specific socket event
 * Automatically cleans up on unmount
 * 
 * @param event - Socket event name (use SocketEvents constants)
 * @param handler - Callback function to handle the event
 * 
 * @example
 * ```tsx
 * import { SocketEvents } from '../constants/socket-events';
 * useSocketEvent(SocketEvents.MESSAGE, (data) => {
 *   console.log('New message:', data);
 *   // Handle message
 * });
 * ```
 */
export function useSocketEvent(
  event: SocketEventType,
  handler: SocketEventHandler
) {
  const { on, off } = useSocket();

  useEffect(() => {
    const unsubscribe = on(event, handler);
    return () => {
      unsubscribe();
      off(event, handler);
    };
  }, [event, handler, on, off]);
}

